// Home-page prompt bar (Nixtio-style): rounded input + send, quick chips,
// and the "Unlock more with Pro Plan" line. Purely a launcher — all chat
// logic stays in ChatView / the existing flows.
//
//   Logged-in: send / "Ask anything" → General Maritime chat (typed text
//   pre-filled); "Troubleshoot equipment" → chat of the first READY manual,
//   or My Manuals if none; "SIRE prep" → the Inspection module.
//   Logged-out: any interaction opens the login modal.
import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { openAuth } from '../lib/uiBus';
import { listMyDocuments } from '../manuals/manualsApi';
import { ManualsOverlay } from '../manuals/ManualsButton';
import ChatView, { equipmentTitle } from '../chat/ChatView';
import FullPageOverlay from './FullPageOverlay';
import { goToPlans } from './PlansPage';
import { BRAND, PAYMENTS_VISIBLE } from '../brand';

export default function PromptBar({ onSirePrep }) {
  const { user, isConfigured } = useAuth();
  const [input, setInput] = useState('');
  const [chat, setChat] = useState(null); // {equipmentPath, title, initialInput}
  const [manualsOpen, setManualsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!isConfigured) return null;

  // Every action funnels through here so logged-out visitors get the modal.
  function guard(action) {
    if (!user) {
      openAuth();
      return;
    }
    action();
  }

  function askGeneral(initialInput) {
    setChat({
      equipmentPath: 'general',
      title: 'General Maritime Assistant',
      initialInput: initialInput || '',
    });
  }

  function submit(e) {
    e?.preventDefault();
    guard(() => {
      askGeneral(input.trim());
      setInput('');
    });
  }

  async function troubleshoot() {
    guard(async () => {
      setBusy(true);
      try {
        const docs = await listMyDocuments();
        const ready = docs.find(d => d.status === 'ready');
        if (ready) {
          setChat({
            equipmentPath: ready.equipment_path,
            title: equipmentTitle(ready.equipment_path),
            subtitle: ready.filename,
            initialInput: '',
          });
        } else {
          setManualsOpen(true); // no manual yet — take them to My Manuals
        }
      } catch {
        setManualsOpen(true);
      } finally {
        setBusy(false);
      }
    });
  }

  return (
    <div className="prompt-wrap no-print">
      <div className="prompt-meta">
        {PAYMENTS_VISIBLE && (
          <button className="linkish" onClick={goToPlans}>
            ✨ Unlock more with Pro Plan →
          </button>
        )}
        <span style={{ color: 'var(--text-3)', marginLeft: 'auto' }}>Powered by {BRAND.name}</span>
      </div>

      <form className="prompt-bar" onSubmit={submit}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onFocus={() => {
            if (!user) openAuth();
          }}
          placeholder="Ask the Maritime Assistant anything…"
          aria-label="Ask the assistant"
        />
        <button type="submit" className="prompt-send" aria-label="Send">
          ➤
        </button>
      </form>

      <div className="prompt-chips">
        <button className="prompt-chip" onClick={troubleshoot} disabled={busy}>
          🔧 Troubleshoot equipment
        </button>
        {/* Inspection is open to everyone — no login guard */}
        <button className="prompt-chip" onClick={onSirePrep}>
          📋 SIRE prep
        </button>
        <button className="prompt-chip" onClick={() => guard(() => askGeneral(''))}>
          🌊 Ask anything
        </button>
      </div>

      {chat && (
        <FullPageOverlay label="Chat">
          <ChatView
            equipmentPath={chat.equipmentPath}
            title={chat.title}
            subtitle={chat.subtitle}
            initialInput={chat.initialInput}
            onBack={() => setChat(null)}
          />
        </FullPageOverlay>
      )}
      {manualsOpen && <ManualsOverlay onClose={() => setManualsOpen(false)} />}
    </div>
  );
}
